import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { postStripePaymentMethod } from '../../api/api';
import Button from '../Button';
import TranslationProvider, { getTranslation } from '../../providers/TranslationProvider';
import './StripeAddCardForm.scss';
import CheckboxSimple from '../Checkbox/CheckboxSimple';
import Loader from '../Loader';
import { STRIPE_FORM_CONTEXT } from '../../constants/paymentProcessors';
import StripeAddCardErrorModal from '../StripeAddCardErrorModal';

const dataTestId = 'StripeAddCardForm';

const getGrecaptcha = () => {
  if (window.grecaptcha && window.grecaptcha.render) {
    return window.grecaptcha;
  }

  return undefined;
};

const renderCaptcha = () => {
  const grecaptcha = getGrecaptcha();

  if (!grecaptcha) {
    setTimeout(renderCaptcha, 500);
    return;
  }

  grecaptcha.render('captcha__container', {
    sitekey: '6Lfw-dIZAAAAAESRBH4JwdgfTXj5LlS1ewlvvCYe',
  });
};

const StripeAddCardForm = ({
  Analytics,
  AutomationMode,
  closeForm,
  context,
  redirectPath,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = useState(null);
  const [showCardValueErrorModal, setShowCardValueErrorModal] = useState(false);
  const [captchaError, setCaptchaError] = useState(null);
  const [isDefaultCard, setDefaultCard] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const captchaElement = document.getElementById('captcha__container');
    if (!AutomationMode && !captchaElement?.children?.length > 0) {
      renderCaptcha();
    }
  });

  const getCaptchaResponse = () => {
    const grecaptcha = getGrecaptcha();
    const captchaResponse = AutomationMode ? true : grecaptcha?.getResponse();

    if (!captchaResponse && !AutomationMode) {
      const captchaErrorMessage = getTranslation({
        translationKey: 'captcha_required',
        fallback: 'Please complete the CAPTCHA before submitting.',
      });

      setCaptchaError(captchaErrorMessage);
    } else if (captchaError) {
      setCaptchaError(null);
    }

    return captchaResponse;
  };

  const handleCloseErrorModal = () => {
    if (showCardValueErrorModal) {
      setShowCardValueErrorModal(false);
      return;
    }

    if (context !== STRIPE_FORM_CONTEXT.profile) {
      window.location.hash = `#${redirectPath}`;
    } else {
      closeForm();
    }
  };

  const toggleDefaultCard = () => {
    setDefaultCard(!isDefaultCard);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);

    const captchaResponse = getCaptchaResponse();
    if (!captchaResponse && !AutomationMode) {
      setIsProcessing(false);
      return;
    }

    const { origin } = window.location;

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${origin}${redirectPath}`,
      },
    });

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      setErrorMessage(error.message);
      setShowCardValueErrorModal(true);
      setIsProcessing(false);
    } else {
      /* eslint-disable camelcase */
      const { payment_method } = setupIntent;

      // prepay shouldn't store data in user wallet
      if (
        context !== STRIPE_FORM_CONTEXT.prepay
        && context !== STRIPE_FORM_CONTEXT.concierge
      ) {
        try {
          const results = await postStripePaymentMethod({ stripe_payment_method_id: payment_method, is_default: isDefaultCard ? 1 : 0 });
          await Analytics.event('add card', {
            'card type': results?.payment_methods[0]?.type,
          });
        } catch (errorData) {
          if (errorData.data?.code === 'prepaid_card_not_supported') {
            // Handle Prepaid Card error
            setErrorMessage(
              getTranslation({
                translationKey: 'prepaid_card_error',
                fallback:
                  "Sorry, we can't accept prepaid cards. Please try another payment method.",
              })
            );
            setShowCardValueErrorModal(true);
          } else if (errorData.data?.code === 'card_vault_err') {
            // Handle Max Vault error
            setErrorMessage(
              getTranslation({
                translationKey: 'please_try_adding_another_payment_method',
                fallback: 'Please try adding another payment method.',
              })
            );
            setShowCardValueErrorModal(true);
          }

          // reset processing state
          setIsProcessing(false);
          return;
        }
      }
      setIsProcessing(false);
      closeForm(payment_method);
      /* eslint-enable camelcase */
      // Your customer will be redirected to your `return_url` if redirect is required. For some payment
      // methods like iDEAL, your customer will be redirected to an intermediate
      // site first to authorize the payment, then redirected to the `return_url`.
    }
  };

  const paymentElementOptions = {
    terms: {
      card: 'never',
    },
    wallets: {
      applePay: 'never',
      googlePay: 'never',
    },
  };

  return (
    <form className="StripeAddCardForm">
      <div className="StripeAddCardForm__inner-container">
        <PaymentElement options={paymentElementOptions} />

        <div className="StripeAddCardForm__extra-details">
          {context !== STRIPE_FORM_CONTEXT.prepay
            && context !== STRIPE_FORM_CONTEXT.concierge && (
              <div className="StripeAddCardForm__default-card-checkbox">
                <CheckboxSimple
                  name="set-default-card"
                  checked={isDefaultCard}
                  onChange={toggleDefaultCard}
                  data-test-id={`${dataTestId}-set-default-checkbox`}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-for, jsx-a11y/label-has-associated-control */}
                <label htmlFor="set-default-card">
                  <TranslationProvider translationKey="set_card_as_default">
                    <span>Set card as default payment method</span>
                  </TranslationProvider>
                </label>
              </div>
          )}
          {context !== STRIPE_FORM_CONTEXT.profile && context !== STRIPE_FORM_CONTEXT.prepay && (
            <TranslationProvider translationKey="card_verification_hold_message">
              <div className="StripeAddCardForm__temporary-hold-disclaimer">To verify your card, our payment processor may place a temporary charge on your account, which will be released back to you within a few days.</div>
            </TranslationProvider>
          )}

          <div id="captcha__container" />
          {captchaError && (
            <div className="StripeAddCardForm__error" role="alert">{captchaError}</div>
          )}
        </div>
      </div>

      <div className="StripeAddCardForm__button-container">
        <Button
          className={cx('Button--lg', {
            'StripeAddCardForm__button--disabled': isProcessing,
          })}
          disabled={!stripe || !elements || isProcessing}
          onClick={handleSubmit}
          data-test-id={`${dataTestId}-submit-button`}
        >
          {isProcessing ? (
            <Loader
              background="white"
              className="StripeAddCardForm__button-processing"
            />
          )
            : (
              <TranslationProvider translationKey="add_card">
                <span>Add Card</span>
              </TranslationProvider>
            )}
        </Button>

        {showCardValueErrorModal && <StripeAddCardErrorModal message={errorMessage} onClose={handleCloseErrorModal} />}
      </div>
    </form>
  );
};

StripeAddCardForm.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  AutomationMode: PropTypes.bool,
  closeForm: PropTypes.func.isRequired,
  context: PropTypes.string,
  redirectPath: PropTypes.string.isRequired,
};

StripeAddCardForm.defaultProps = {
  AutomationMode: false,
  context: STRIPE_FORM_CONTEXT.booking,
};

export default StripeAddCardForm;
